// frontend/src/features/ups/modals/DriverDetailsModal.js
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  uploadDriverPhoto as svcUpload,
  deleteDriverPhoto as svcDelete,
} from '../../../services/driverService';
import {
  upsertDriver,
  selectDriverById,
} from '../../drivers/driverSlice';

export default function DriverDetailsModal({ driver, onClose }) {
  // Hooks must always be at the top, never inside conditionals
  const closeBtnRef = useRef(null);
  const dispatch = useDispatch();

  const [isBusy, setIsBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Read the LIVE driver from Redux (fallback to prop for initial mount)
  const liveDriver = useSelector((state) =>
    driver?.id != null ? selectDriverById(state, driver.id) : null
  );

  // Focus the close button on mount
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Now derive data from either live Redux driver or the prop
  const d = liveDriver || driver || {};

  const safe = (v) => v ?? '—';
  const safeDate = (v) => (v ? new Date(v).toLocaleString() : '—');

  const {
    id,
    name,
    vehicle_registration_number,
    vrn, // sometimes used in UI code
    phone,
    email,
    agency_name,
    agency,
    submission_date,
    // ban-related (optional)
    banned_at,
    ban_reason,
    // photo-related (from backend)
    photo_url,
    photo_mime,
    photo_updated_at,
  } = d;

  // Early exit AFTER hooks have run
  if (!id) return null;

  // --------------------------
  // Handlers: Upload & Delete
  // --------------------------

  // Upload/replace photo
  async function handleUploadPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Optional: enforce client-side constraints (server also validates)
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setErrorMsg('Only JPEG, PNG, or WEBP images are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg('Image must be 5MB or smaller.');
      return;
    }

    try {
      setIsBusy(true);
      setErrorMsg('');

      // svcUpload now returns the UPDATED driver object
      const updated = await svcUpload(id, file);
      if (updated) {
        dispatch(upsertDriver(updated));
      }
    } catch (err) {
      console.error(err);
      setErrorMsg('Could not upload photo. Please try again.');
    } finally {
      setIsBusy(false);
      if (e.target) e.target.value = '';
    }
  }

  // Delete photo
  async function handleRemovePhoto() {
    try {
      setIsBusy(true);
      setErrorMsg('');

      // svcDelete now returns the UPDATED driver object
      const updated = await svcDelete(id);
      if (updated) {
        dispatch(upsertDriver(updated));
      }
    } catch (err) {
      console.error(err);
      setErrorMsg('Could not remove photo. Please try again.');
    } finally {
      setIsBusy(false);
    }
  }

  // Cache-busting for the photo URL
  const ver = photo_updated_at
    ? new Date(photo_updated_at).getTime()
    : Date.now();

  const src = photo_url
    ? `${photo_url}?v=${ver}`
    : '/img/placeholder-avatar.png';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
      aria-hidden="true"
    >
      <div
        className="bg-white rounded-lg shadow-md p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="driver-details-title"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 id="driver-details-title" className="text-xl font-semibold">
            Driver Details
          </h2>
          <button
            onClick={onClose}
            ref={closeBtnRef}
            aria-label="Close driver details"
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
          >
            &times;
          </button>
        </div>

        {/* Error / status bar */}
        {errorMsg && (
          <div className="mb-3 p-2 rounded bg-red-50 text-red-700 text-sm border border-red-200">
            {errorMsg}
          </div>
        )}

        {/* Photo block */}
        <div className="mb-4">
          <p className="font-semibold mb-2">Photo</p>

          {/* Current photo (if any) */}
          {photo_url ? (
            <div className="flex items-start gap-3">
              <img
                key={photo_updated_at || `no-photo-${id}`} // force remount on change
                src={src}
                alt={`${safe(name)} photo`}
                className="w-24 h-24 object-cover rounded border"
              />
              <div className="flex-1">
                <p className="text-sm text-gray-600">
                  Type: {safe(photo_mime)}
                  <br />
                  Updated: {safeDate(photo_updated_at)}
                </p>

                <div className="flex gap-2 mt-2">
                  {/* Replace photo: label styled like a button */}
                  <label
                    className={`px-3 py-1 rounded text-white ${
                      isBusy
                        ? 'bg-gray-400'
                        : 'bg-blue-600 hover:bg-blue-700'
                    } cursor-pointer`}
                  >
                    {isBusy ? 'Working…' : 'Replace photo'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleUploadPhoto}
                      disabled={isBusy}
                    />
                  </label>

                  <button
                    onClick={handleRemovePhoto}
                    disabled={isBusy}
                    className={`px-3 py-1 rounded border ${
                      isBusy
                        ? 'bg-gray-100 text-gray-400'
                        : 'bg-white hover:bg-gray-50 text-red-600 border-red-300'
                    }`}
                  >
                    Remove photo
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // No photo yet — show uploader
            <div className="flex items-center gap-3">
              <div className="w-24 h-24 rounded border bg-gray-50 flex items-center justify-center text-gray-400">
                No photo
              </div>
              <label
                className={`px-3 py-1 rounded text-white ${
                  isBusy
                    ? 'bg-gray-400'
                    : 'bg-blue-600 hover:bg-blue-700'
                } cursor-pointer`}
              >
                {isBusy ? 'Working…' : 'Upload photo'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleUploadPhoto}
                  disabled={isBusy}
                />
              </label>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-2">
          <p>
            <strong>Name:</strong> {safe(name)}
          </p>
          <p>
            <strong>Vehicle Reg.:</strong>{' '}
            {safe(vehicle_registration_number || vrn)}
          </p>
          <p>
            <strong>Phone:</strong> {safe(phone)}
          </p>
          <p>
            <strong>Email:</strong> {safe(email)}
          </p>
          <p>
            <strong>Agency:</strong>{' '}
            {safe(agency_name || agency?.name)}
          </p>
          <p>
            <strong>Submitted:</strong> {safeDate(submission_date)}
          </p>

          {(banned_at || ban_reason) && (
            <div className="mt-3 rounded border border-red-200 bg-red-50 p-3">
              <p className="font-semibold text-red-700">Ban Info</p>
              <p>
                <strong>Banned At:</strong> {safeDate(banned_at)}
              </p>
              <p>
                <strong>Reason:</strong> {safe(ban_reason)}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
